import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// Statement classes rendered as colour-coded badges so the type of every row is
// readable at a glance without opening the account.
export const ACCOUNT_TYPE_STYLES = {
  asset: { label: "Asset", bg: "#2a2962" },
  liability: { label: "Liability", bg: "#e42320" },
  equity: { label: "Equity", bg: "#6f42c1" },
  income: { label: "Income", bg: "#198754" },
  expense: { label: "Expense", bg: "#c46b00" },
};

export const AccountTypeBadge = ({ type }) => {
  const style = ACCOUNT_TYPE_STYLES[type];

  if (!style) {
    return <span className="text-muted small">-</span>;
  }

  return (
    <span
      className="badge"
      style={{
        backgroundColor: style.bg,
        color: "#fff",
        fontSize: "0.7rem",
        letterSpacing: "0.4px",
        padding: "0.4em 0.75em",
        textTransform: "uppercase",
      }}
    >
      {style.label}
    </span>
  );
};

// Tree guide geometry: every depth level is one GUIDE_WIDTH column, with its
// line drawn GUIDE_X px in, which is the centre of the toggle above it.
const GUIDE_WIDTH = 24;
const GUIDE_X = 11;
const GUIDE = "linear-gradient(#c9ccdc, #c9ccdc)";

const TreeView = ({ data, actionTemplate, deleteData, toolbarExtra }) => {
  // Get all node IDs recursively to expand all by default
  const getAllNodeIds = (nodes) => {
    const ids = new Set();
    const traverse = (nodeList) => {
      nodeList.forEach((node) => {
        ids.add(node.id);
        if (node.children_recursive && node.children_recursive.length > 0) {
          traverse(node.children_recursive);
        }
      });
    };
    traverse(nodes || []);
    return ids;
  };

  // Total node count (roots + every descendant), for the toolbar summary.
  const countNodes = (nodes) =>
    (nodes || []).reduce(
      (total, node) => total + 1 + countNodes(node.children_recursive),
      0,
    );

  const [expandedNodes, setExpandedNodes] = useState(() => getAllNodeIds(data));

  // Update expanded nodes when data changes
  useEffect(() => {
    setExpandedNodes(getAllNodeIds(data));
  }, [data]);

  const allNodeIds = getAllNodeIds(data);
  const totalNodes = countNodes(data);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    setExpandedNodes(getAllNodeIds(data));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // `lineage[k]` says whether the node's ancestor at depth k+1 (the node itself
  // for the last entry) is the last of its siblings. That decides, column by
  // column, whether a guide line keeps running down or stops at an elbow.
  const renderTreeNode = (node, level = 0, lineage = []) => {
    const hasChildren =
      node.children_recursive && node.children_recursive.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const cellPadding = {
      paddingTop: "12px",
      paddingBottom: "12px",
    };

    return (
      <React.Fragment key={node.id}>
        <tr
          className={`tree-node tree-level-${level} ${
            hasChildren ? "has-children" : "leaf-node"
          }`}
          style={{
            backgroundColor: hasChildren
              ? level === 0
                ? "rgba(42, 41, 98, 0.07)"
                : "rgba(42, 41, 98, 0.035)"
              : "white",
            borderLeft: hasChildren
              ? level === 0
                ? "5px solid #2a2962"
                : "3px solid #8483b5"
              : "2px solid transparent",
            borderBottom: "1px solid #e9ecef",
            fontWeight: hasChildren ? (level === 0 ? "600" : "500") : "400",
          }}
        >
          {/* No vertical padding on this cell: the guide columns stretch the
              full row height so the lines join up from one row to the next. */}
          <td
            className="border-0"
            style={{ padding: "0 12px 0 16px", height: "1px" }}
          >
            <div
              className="d-flex align-items-stretch"
              style={{ height: "100%", minHeight: "48px" }}
            >
              {lineage.map((isLast, column) => {
                const isElbow = column === level - 1;
                const layers = [];

                if (isElbow) {
                  // Down from the parent, then across to this node; stop
                  // half-way for the last sibling so the line ends in an L.
                  layers.push({
                    image: GUIDE,
                    size: `1px ${isLast ? "50%" : "100%"}`,
                    position: `${GUIDE_X}px 0`,
                  });
                  layers.push({
                    image: GUIDE,
                    size: `${GUIDE_WIDTH - GUIDE_X}px 1px`,
                    position: `${GUIDE_X}px 50%`,
                  });
                } else if (!lineage[column]) {
                  // An ancestor with siblings still to come keeps its line.
                  layers.push({
                    image: GUIDE,
                    size: "1px 100%",
                    position: `${GUIDE_X}px 0`,
                  });
                }

                return (
                  <span
                    key={column}
                    aria-hidden="true"
                    style={{
                      width: `${GUIDE_WIDTH}px`,
                      flexShrink: 0,
                      backgroundImage: layers.map((l) => l.image).join(", "),
                      backgroundSize: layers.map((l) => l.size).join(", "),
                      backgroundPosition: layers
                        .map((l) => l.position)
                        .join(", "),
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                );
              })}

              {/* The toggle column also carries the start of the line down to
                  this node's own children while it is expanded. */}
              <span
                style={{
                  width: `${GUIDE_WIDTH}px`,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: hasChildren && isExpanded ? GUIDE : "none",
                  backgroundSize: "1px 50%",
                  backgroundPosition: `${GUIDE_X}px 100%`,
                  backgroundRepeat: "no-repeat",
                }}
              >
                {hasChildren ? (
                  <span
                    className={`tree-toggle ${
                      isExpanded ? "expanded" : "collapsed"
                    }`}
                    onClick={() => toggleNode(node.id)}
                    style={{
                      cursor: "pointer",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "5px",
                      backgroundColor: level === 0 ? "#2a2962" : "#8483b5",
                      color: "white",
                      fontSize: "0.65rem",
                      lineHeight: 1,
                      position: "relative",
                      zIndex: 1,
                    }}
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    <i
                      className={`fa ${isExpanded ? "fa-minus" : "fa-plus"}`}
                      aria-hidden="true"
                    ></i>
                  </span>
                ) : (
                  <span
                    aria-hidden="true"
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#b6bacb",
                      position: "relative",
                      zIndex: 1,
                    }}
                  />
                )}
              </span>

              <div
                className="d-flex align-items-center ms-2"
                style={cellPadding}
              >
                <span
                  className="account-name"
                  style={{
                    fontSize:
                      hasChildren && level === 0 ? "1.05rem" : "0.95rem",
                    fontWeight: hasChildren
                      ? level === 0
                        ? "700"
                        : "600"
                      : "400",
                    color: hasChildren ? "#2a2962" : "#495057",
                  }}
                >
                  {node.name}
                </span>
                {hasChildren && (
                  <span
                    className="text-muted ms-2"
                    style={{ fontSize: "0.75rem" }}
                  >
                    ({node.children_recursive.length})
                  </span>
                )}
              </div>
            </div>
          </td>
          <td className="border-0" style={cellPadding}>
            <span
              className="badge bg-light text-dark font-monospace"
              style={{ fontSize: "0.75rem", padding: "0.4em 0.8em" }}
            >
              {node.code}
            </span>
          </td>
          <td className="border-0" style={cellPadding}>
            <AccountTypeBadge type={node.account_type} />
          </td>
          <td className="border-0" style={cellPadding}>
            <span
              className={`fw-bold ${
                parseFloat(node.balance || 0) >= 0
                  ? "text-success"
                  : "text-danger"
              }`}
            >
              {parseFloat(node.balance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </td>
          <td className="border-0" style={cellPadding}>
            <span className="text-muted small">{node.description || "-"}</span>
          </td>
          <td className="border-0 text-center" style={cellPadding}>
            {actionTemplate ? (
              actionTemplate(node, deleteData)
            ) : (
              <Link
                to={{ pathname: "/edit-chart-of-account/" + node.id }}
                className="btn btn-outline-primary btn-sm rounded-pill"
                title="Edit Account"
              >
                <i className="fa fa-edit" aria-hidden="true"></i>
              </Link>
            )}
          </td>
        </tr>
        {hasChildren &&
          isExpanded &&
          node.children_recursive.map((child, index) =>
            renderTreeNode(child, level + 1, [
              ...lineage,
              index === node.children_recursive.length - 1,
            ]),
          )}
      </React.Fragment>
    );
  };

  const headerCellStyle = {
    color: "#fff",
    fontSize: "0.8rem",
    letterSpacing: "0.5px",
  };

  return (
    <div
      className="tree-view-container"
      style={{
        background: "#fff",
        border: "1px solid #e6e8f0",
        borderRadius: "10px",
        boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
        overflow: "hidden",
      }}
    >
      <div
        className="tree-controls d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid #e6e8f0",
        }}
      >
        <div className="control-buttons d-flex align-items-center flex-wrap gap-2">
          <button
            className="btn btn-sm shadow-sm"
            onClick={expandAll}
            disabled={expandedNodes.size === allNodeIds.size}
            style={{
              backgroundColor: "#2a2962",
              color: "#fff",
              borderRadius: "6px",
              fontWeight: "500",
              border: "1px solid #2a2962",
            }}
          >
            <i className="fa fa-plus-square-o me-2"></i>
            Expand All
          </button>
          <button
            className="btn btn-sm btn-outline-secondary shadow-sm"
            onClick={collapseAll}
            disabled={expandedNodes.size === 0}
            style={{ borderRadius: "6px", fontWeight: "500" }}
          >
            <i className="fa fa-minus-square-o me-2"></i>
            Collapse All
          </button>
          {toolbarExtra}
        </div>
        <div
          className="tree-info text-muted small"
          style={{
            padding: "5px 13px",
            background: "#f2f3f9",
            borderRadius: "16px",
            fontWeight: 600,
            color: "#6b7280",
          }}
        >
          <i className="fa fa-info-circle me-1"></i>
          {data ? data.length : 0} root / {totalNodes} accounts
        </div>
      </div>
      <div className="table-responsive" style={{ background: "#fff" }}>
        <table className="table table-hover mb-0 modern-table">
          <thead style={{ background: "#2a2962" }}>
            <tr>
              <th
                className="border-0 text-uppercase fw-bold py-3 ps-3"
                style={headerCellStyle}
              >
                <i className="fa fa-sitemap me-2"></i>Account Name
              </th>
              <th
                className="border-0 text-uppercase fw-bold py-3"
                style={headerCellStyle}
              >
                <i className="fa fa-code me-2"></i>Code
              </th>
              <th
                className="border-0 text-uppercase fw-bold py-3"
                style={headerCellStyle}
              >
                <i className="fa fa-tag me-2"></i>Account Type
              </th>
              <th
                className="border-0 text-uppercase fw-bold py-3"
                style={headerCellStyle}
              >
                <i className="me-2">৳</i>Amount
              </th>
              <th
                className="border-0 text-uppercase fw-bold py-3"
                style={headerCellStyle}
              >
                <i className="fa fa-file-text me-2"></i>Description
              </th>
              <th
                className="border-0 text-uppercase fw-bold py-3 text-center"
                style={headerCellStyle}
              >
                <i className="fa fa-cogs me-2"></i>Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((node) => renderTreeNode(node))
            ) : (
              <tr>
                <td colSpan="6" className="text-center text-muted py-5">
                  <i className="fa fa-folder-open-o fa-2x d-block mb-2"></i>
                  No accounts match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TreeView;
